import { Divider, IconButton, Text, useOutsideClick } from '@chakra-ui/react';
import { useDndContext, useDndMonitor, useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { IconChevronDownSmall } from 'central-icons/IconChevronDownSmall';
import { IconCrossLarge } from 'central-icons/IconCrossLarge';
import { IconDotGrid1x3Vertical } from 'central-icons/IconDotGrid1x3Vertical';
import { IconEditBig } from 'central-icons/IconEditBig';
import { IconMagnifyingGlass2 } from 'central-icons/IconMagnifyingGlass2';
import { IconPlusLarge } from 'central-icons/IconPlusLarge';
import classNames from 'classnames';
import mixpanel from 'mixpanel-browser';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { BuildADashTrackingEvents } from '@revelio/core';
import {
  Badge,
  BadgeColor,
  Button,
  Input,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Popover,
  RevelioTooltip,
  IconButton as UiIconButton,
  useDisclosure,
} from '@revelio/ui';

import { useAfterMenuClose } from '../../../shared/hooks/useAfterMenuClose';
import type { TabItem } from '../hooks/useTabOrder';
import type { AnalyticsEntity } from '../utils/revelio-primary-filter-entity-switch';
import {
  buildPathFromEntityType,
  entityTypeToFilterId,
  filterIdToEntityType,
} from '../utils/routing-helpers';
import { DraggableTabLink } from './DraggableTabLink';
import { useEditCurrentPage } from './PageActionBar/useEditCurrentPage';
import { usePageManager } from './PageManagerProvider/PageManagerProvider';
import styles from './PagesMenu.module.css';
import layoutStyles from './pages-menu.module.css';
import {
  PAGES_MENU_LIST_ID,
  PAGES_MENU_SURFACE_ID,
  PAGES_MENU_TRIGGER_ID,
} from './tab-drag-collision';

const EmptyMessage = ({ children }: { children: ReactNode }) => (
  <Text className={styles.emptyMessage}>{children}</Text>
);

type PagesMenuProps = {
  entity: AnalyticsEntity;
  menuTabs: TabItem[];
  activeMenuTabId: string | null;
};

export const PagesMenu = ({
  entity,
  menuTabs,
  activeMenuTabId,
}: PagesMenuProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    createPageDraft,
    deletePage,
    clonePage,
    clonePageDraft,
    updatePage,
    editPage,
  } = usePageManager();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    canEdit: canEditCurrentPage,
    label: editCurrentPageLabel,
    editCurrentPage,
  } = useEditCurrentPage();
  const [editingId, setEditingId] = useState<string | null>(null);
  const { runAfterClose, onCloseAutoFocus } = useAfterMenuClose();
  const popoverContentRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);

  // Single source of truth: track whether a drag is active or just ended.
  // Real outside clicks close; drag drops (which fire a synthetic click on
  // the bar) are ignored. The popover's own dismissal is switched off
  // (onInteractOutside below) so it doesn't fight us: it fires on pointer-down,
  // before dnd-kit knows a press on a bar tab is the start of a drag into this
  // menu, whereas this listener decides on mouse-up with the drag settled.
  const { active: dragActive, measureDroppableContainers } = useDndContext();
  const ignoreOutsideUntilRef = useRef(0);

  useDndMonitor({
    onDragStart: () => {
      ignoreOutsideUntilRef.current = Number.POSITIVE_INFINITY;
    },
    onDragEnd: () => {
      ignoreOutsideUntilRef.current = Date.now() + 200;
    },
    onDragCancel: () => {
      ignoreOutsideUntilRef.current = Date.now() + 200;
    },
  });

  useOutsideClick({
    ref: popoverContentRef,
    handler: (e) => {
      if (!isOpen) return;
      if (dragActive) return;
      if (Date.now() < ignoreOutsideUntilRef.current) return;
      // Let the trigger's own click toggle handle close so we don't
      // close-then-reopen on the same click.
      if (e.target instanceof Node && triggerRef.current?.contains(e.target)) {
        return;
      }
      onClose();
    },
  });
  const [showSearch, setShowSearch] = useState(false);
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showSearch) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    } else {
      setSearch('');
    }
  }, [showSearch]);

  useEffect(() => {
    if (!isOpen) {
      setShowSearch(false);
      setSearch('');
    }
  }, [isOpen]);

  const filteredMenuTabs = search
    ? menuTabs.filter((t) =>
        t.label.toLowerCase().includes(search.toLowerCase())
      )
    : menuTabs;

  const highlight = (label: string): ReactNode => {
    if (!search) return label;
    const idx = label.toLowerCase().indexOf(search.toLowerCase());
    if (idx === -1) return label;
    return (
      <>
        {label.slice(0, idx)}
        <mark>{label.slice(idx, idx + search.length)}</mark>
        {label.slice(idx + search.length)}
      </>
    );
  };

  // Drop zone for bar -> menu drags (works even when popover is closed since
  // the trigger button is always rendered).
  const { setNodeRef: setTriggerDropRef, isOver: isTriggerOver } = useDroppable(
    {
      id: PAGES_MENU_TRIGGER_ID,
    }
  );
  const { setNodeRef: setSurfaceDropRef } = useDroppable({
    id: PAGES_MENU_SURFACE_ID,
  });

  // Auto-open the popover when a bar tab is dragged over the trigger so the
  // user can land the drop on a specific position inside the menu.
  useEffect(() => {
    if (isTriggerOver && !isOpen) onOpen();
  }, [isTriggerOver, isOpen, onOpen]);

  // A menu opened mid-drag registers its droppables before Radix has placed
  // the surface, so dnd-kit measures them off-screen and nothing in the menu
  // can be hovered. Measure again once the surface has been positioned.
  const isDragging = dragActive !== null;
  useEffect(() => {
    if (!isOpen || !isDragging) return;
    const ids = [PAGES_MENU_SURFACE_ID, ...menuTabs.map((t) => t.id)];
    const frame = requestAnimationFrame(() => measureDroppableContainers(ids));
    return () => cancelAnimationFrame(frame);
  }, [isOpen, isDragging, menuTabs, measureDroppableContainers]);

  const handleCreate = () => {
    const entityFilterId = entityTypeToFilterId(entity);
    if (!entityFilterId) return;
    onClose();
    createPageDraft(filterIdToEntityType(entityFilterId));
    mixpanel.track(BuildADashTrackingEvents.CREATE_NEW_PAGE, {
      entityType: entityFilterId,
    });
  };

  const triggerActive = isOpen || isTriggerOver || activeMenuTabId !== null;
  const menuCount = menuTabs.length;

  return (
    <Popover
      open={isOpen}
      onOpenChange={(next) => (next ? onOpen() : onClose())}
      placement="bottom-end"
      sideOffset={8}
      className={styles.popover}
      data-testid="pages-menu-popover"
      onInteractOutside={(event) => event.preventDefault()}
      trigger={
        <div
          ref={(node) => {
            setTriggerDropRef(node);
            triggerRef.current = node;
          }}
          className={classNames(
            styles.triggerBox,
            isTriggerOver && styles.triggerBoxDropTarget
          )}
        >
          <Button
            variant="bare"
            aria-label={
              menuCount > 0
                ? `+${menuCount} more ${menuCount === 1 ? 'page' : 'pages'}`
                : 'Open pages menu'
            }
            data-testid="pages-menu-trigger"
            className={classNames(
              styles.trigger,
              triggerActive && styles.triggerActive
            )}
          >
            {menuCount > 0 && (
              <span data-testid="pages-menu-count">+{menuCount}</span>
            )}
            <IconChevronDownSmall size={16} className={styles.triggerChevron} />
          </Button>
          {activeMenuTabId !== null && (
            <div className={styles.badgeBox}>
              <Badge
                data-testid="pages-menu-active-badge"
                color={BadgeColor.LIGHT_BLUE}
                size="sm"
              />
            </div>
          )}
        </div>
      }
    >
      <div
        ref={(node) => {
          setSurfaceDropRef(node);
          popoverContentRef.current = node;
        }}
      >
        <div className={layoutStyles.header}>
          <Text className={styles.headerText}>Pages</Text>
          <div className={layoutStyles.headerActions}>
            {menuTabs.length >= 5 && (
              <IconButton
                aria-label="Toggle search"
                data-testid="pages-menu-search-toggle"
                icon={<IconMagnifyingGlass2 size={14} />}
                size="xs"
                borderRadius="var(--ui-radius-xs)"
                colorScheme={showSearch ? 'gray' : 'navyBlue'}
                variant={showSearch ? 'solid' : 'ghost'}
                className={styles.chakraIconButton}
                aria-pressed={showSearch}
                onClick={() => setShowSearch((prev) => !prev)}
              />
            )}
            {canEditCurrentPage && (
              <RevelioTooltip label={editCurrentPageLabel}>
                <UiIconButton
                  aria-label={editCurrentPageLabel}
                  data-testid="pages-menu-edit-current"
                  icon={<IconEditBig size={14} />}
                  size="sm"
                  shape="squared"
                  variant="ghost"
                  className={styles.navyIcon}
                  onClick={() => {
                    onClose();
                    editCurrentPage();
                  }}
                />
              </RevelioTooltip>
            )}
            <UiIconButton
              aria-label="Create new page"
              data-testid="pages-menu-create"
              icon={<IconPlusLarge size={14} />}
              size="sm"
              shape="squared"
              variant="ghost"
              className={styles.navyIcon}
              onClick={handleCreate}
            />
          </div>
        </div>
        {showSearch && (
          <div className={layoutStyles.searchBar}>
            <Input
              size="sm"
              ref={searchInputRef}
              data-testid="pages-menu-search-input"
              placeholder="Search pages..."
              wrapperClassName={styles.searchInput}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              endSlot={
                search && (
                  <UiIconButton
                    aria-label="Clear search"
                    size="sm"
                    variant="ghost"
                    shape="rounded"
                    className={styles.navyIcon}
                    icon={<IconCrossLarge size={12} />}
                    onClick={() => {
                      setSearch('');
                      searchInputRef.current?.focus();
                    }}
                  />
                )
              }
            />
          </div>
        )}
        <Divider className={styles.divider} />
        <SortableContext
          id={PAGES_MENU_LIST_ID}
          items={filteredMenuTabs.map((t) => t.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className={styles.scrollArea}>
            {(() => {
              if (menuTabs.length === 0) {
                return (
                  <EmptyMessage>Drag tabs here or add a new page</EmptyMessage>
                );
              }
              if (filteredMenuTabs.length === 0) {
                return <EmptyMessage>No pages found</EmptyMessage>;
              }
              return filteredMenuTabs.map((tab) => {
                const page = tab.page;
                // Analytics tabs don't have a `page` object (templates
                // live in localTemplates, not the user's pages), but the
                // template id matches the RevelioPageId so editPage /
                // clonePage resolve via findPage().
                const actionPageId = page?.id ?? tab.revelioPageId;
                const trailing = actionPageId ? (
                  <Menu>
                    <MenuButton>
                      <IconButton
                        icon={<IconDotGrid1x3Vertical size={14} />}
                        aria-label="Page actions"
                        data-testid={`pages-menu-${tab.kind}-${tab.id}-actions`}
                        size="xs"
                        variant="ghost"
                        colorScheme="navyBlue"
                        className={styles.chakraIconButton}
                        borderRadius="var(--ui-radius-xs)"
                        marginRight="2"
                        tabIndex={0}
                        visibility="hidden"
                        _groupHover={{ visibility: 'visible' }}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </MenuButton>
                    <MenuList
                      portal={false}
                      align="end"
                      sideOffset={0}
                      minWidth="6rem"
                      data-testid={`pages-menu-${tab.kind}-${tab.id}-menu-list`}
                      onCloseAutoFocus={onCloseAutoFocus}
                    >
                      <MenuItem
                        onClick={() => {
                          onClose();
                          if (page) {
                            editPage(actionPageId);
                          } else {
                            // Revelio templates are immutable; "edit"
                            // opens the template as an editable draft
                            // via the clone route (editMode=true).
                            navigate(
                              buildPathFromEntityType(entity, {
                                templateId: actionPageId,
                                action: 'clone',
                                search: location.search,
                              })
                            );
                          }
                          mixpanel.track(BuildADashTrackingEvents.EDIT_PAGE, {
                            page_id: actionPageId,
                          });
                        }}
                      >
                        Edit Page
                      </MenuItem>
                      {page && (
                        <MenuItem
                          onClick={() =>
                            runAfterClose(() => setEditingId(page.id))
                          }
                        >
                          Rename
                        </MenuItem>
                      )}
                      <MenuItem
                        onClick={() => {
                          // Analytics templates aren't in local pages, so
                          // clonePage's local-only lookup would no-op —
                          // route them through clonePageDraft instead.
                          if (page) {
                            clonePage(actionPageId);
                          } else {
                            onClose();
                            clonePageDraft(actionPageId);
                          }
                          mixpanel.track(BuildADashTrackingEvents.CLONE_PAGE, {
                            page_id: actionPageId,
                          });
                        }}
                      >
                        Make a Copy
                      </MenuItem>
                      {page && (
                        <>
                          <MenuDivider />
                          <MenuItem
                            onClick={() => {
                              deletePage(page.id);
                              mixpanel.track(
                                BuildADashTrackingEvents.DELETE_PAGE,
                                { page_id: page.id }
                              );
                            }}
                          >
                            Delete
                          </MenuItem>
                        </>
                      )}
                    </MenuList>
                  </Menu>
                ) : null;
                return (
                  <DraggableTabLink
                    key={tab.id}
                    tab={tab}
                    variant="menu"
                    isActive={activeMenuTabId === tab.id}
                    onNavigate={onClose}
                    testId={`pages-menu-${tab.kind}-${tab.id}`}
                    editing={page ? editingId === page.id : false}
                    editableValue={tab.label}
                    onRename={(val) => {
                      if (page && val && val !== page.name) {
                        updatePage(page.id, { ...page, name: val });
                      }
                      setEditingId(null);
                    }}
                    trailing={trailing}
                  >
                    {highlight(tab.label)}
                  </DraggableTabLink>
                );
              });
            })()}
          </div>
        </SortableContext>
      </div>
    </Popover>
  );
};
